import * as DialogPrimitive from '@radix-ui/react-dialog'
import clsx from 'clsx'
import * as React from 'react'
import { ButtonHTMLAttributes } from 'react'

import CircularButton from '@/components-latest/primitives/CircularButton'
import { Cross } from '@/components-latest/primitives/Icons'
import DialogGoBack from '@/components-latest/primitives/Icons/DialogGoBack'

import Styles from './Dialog.module.scss'
const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={clsx(Styles.overlay, className)}
    data-testid="dialog-overlay"
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={clsx(Styles.content, className)}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(Styles.header, className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(Styles.body, className)} {...props} />
)
DialogBody.displayName = 'DialogBody'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(Styles.footer, className)} {...props} />
)

DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx(Styles.title, className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={clsx(Styles.description, className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <CircularButton variant="flat" size="normal" asChild className={className}>
    <DialogPrimitive.Close className={Styles.close} ref={ref} {...props}>
      <Cross data-testid="dialog-close-button" className={Styles.closeIcon} />
    </DialogPrimitive.Close>
  </CircularButton>
))
DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogReturn = ({
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <CircularButton
    variant="flat"
    size="normal"
    asChild
    className={className}
    {...props}>
    <DialogGoBack
      data-testid="dialog-return-button"
      className={Styles.return}
    />
  </CircularButton>
)
DialogReturn.displayName = 'DialogReturn'

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogReturn,
  DialogTitle,
  DialogTrigger
}
